import { useCallback } from 'react';
import { useMediaQuery } from '@/hooks/use-media-query';
import { DESKTOP_LAYOUT_QUERY } from '@/lib/desktop-shell/desktop-layout';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { activatePanelTab, openPanelTab, showPanelView } from '@/lib/side-panel/panel-tabs';
import type { SidePanelView } from '@/lib/side-panel/side-panel-items';
import {
  isAssistantView,
  PROJECT_SETTINGS_VIEW,
  PROJECT_TASKS_VIEW,
  PROJECT_VIEWS,
} from './assistant-views';

export { PROJECT_SETTINGS_VIEW } from './assistant-views';

/**
 * Opens a project's tabs in its conversation's panel and shows `view` (Tasks by default). With
 * `onlyIfClosed`, a panel the user arranged already is left as it is.
 */
export function useOpenAssistantPanel() {
  const store = useDesktopShellStoreApi();
  const phone = !useMediaQuery(DESKTOP_LAYOUT_QUERY);
  return useCallback(
    (conversationId: string, options: { view?: SidePanelView; onlyIfClosed?: boolean } = {}) => {
      const state = store.getState();
      if (state.chatId !== conversationId) return;
      const open = state.panelTabs.tabs.some((tab) => isAssistantView(tab.view));
      if (options.onlyIfClosed && open) return;
      // A phone shows the project page: one tab holds every section.
      const views = phone ? [PROJECT_SETTINGS_VIEW] : PROJECT_VIEWS;
      const wanted = phone ? PROJECT_SETTINGS_VIEW : (options.view ?? PROJECT_TASKS_VIEW);
      state.setPanelTabs((current) => {
        let tabs = current;
        for (const view of views)
          if (!tabs.tabs.some((tab) => tab.view === view))
            tabs = {
              ...openPanelTab(tabs, { id: crypto.randomUUID(), view }),
              activeId: tabs.activeId,
            };
        const shown = showPanelView(tabs, wanted);
        return shown.activeId === null ? shown : activatePanelTab(shown, shown.activeId);
      });
      // On a phone the page opens from the header's button, not by itself.
      if (!phone || !options.onlyIfClosed) state.setSidePanelOpen(true);
    },
    [store, phone],
  );
}
